import React, { useState, useEffect } from 'react';
import {
  Box, Button, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Select, MenuItem, FormControl, InputLabel,
  Chip, Snackbar, Alert, Tooltip, CircularProgress
} from '@mui/material';
import { Add, Edit, Delete, InfoOutlined } from '@mui/icons-material';
import plantillasService from '../services/plantillasService';
import { TIPO_OPTIONS } from './clients/parametros';

const PLACEHOLDERS = [
  { key: '{fecha}', desc: 'Fecha del documento' },
  { key: '{nombreCliente}', desc: 'Nombre del cliente' },
  { key: '{aumentoGeneral}', desc: 'Porcentaje de aumento acumulado' },
  { key: '{bonoNavideno}', desc: 'Monto del bono navideño' },
  { key: '{incrementoAdicional}', desc: 'Porcentaje adicional según tipo de cliente' },
  { key: '{detalleAmbito}', desc: 'Descripción del ámbito (ej: espacios verdes...)' },
];

const EMPTY_FORM = { nombre: '', tipo_cliente: '', cuerpo: '', activa: 1 };

const getTipoLabel = (value) => {
  if (value === null || value === undefined || value === '') return 'Todos';
  const tipo = TIPO_OPTIONS.find(t => t.value === Number(value));
  return tipo ? tipo.label : 'Desconocido';
};

export default function PlantillasTab() {
  const [plantillas, setPlantillas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = (message, severity = 'success') =>
    setSnackbar({ open: true, message, severity });

  const loadPlantillas = async () => {
    setLoading(true);
    try {
      const data = await plantillasService.getAll();
      setPlantillas(data);
    } catch {
      showSnackbar('Error al cargar plantillas', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadPlantillas(); }, []);

  const handleOpenCreate = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setDialogOpen(true);
  };

  const handleOpenEdit = (plantilla) => {
    setForm({
      nombre: plantilla.nombre,
      tipo_cliente: plantilla.tipo_cliente ?? '',
      cuerpo: plantilla.cuerpo,
      activa: plantilla.activa,
    });
    setEditingId(plantilla.id);
    setDialogOpen(true);
  };

  const handleOpenDelete = (id) => {
    setDeletingId(id);
    setDeleteDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.nombre.trim() || !form.cuerpo.trim()) {
      showSnackbar('El nombre y el cuerpo son obligatorios', 'warning');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        tipo_cliente: form.tipo_cliente === '' ? null : Number(form.tipo_cliente),
      };
      if (editingId) {
        await plantillasService.update(editingId, payload);
        showSnackbar('Plantilla actualizada');
      } else {
        await plantillasService.create(payload);
        showSnackbar('Plantilla creada');
      }
      setDialogOpen(false);
      loadPlantillas();
    } catch {
      showSnackbar('Error al guardar la plantilla', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await plantillasService.remove(deletingId);
      showSnackbar('Plantilla eliminada');
      setDeleteDialogOpen(false);
      loadPlantillas();
    } catch {
      showSnackbar('Error al eliminar la plantilla', 'error');
    }
  };

  const insertPlaceholder = (key) => {
    setForm(prev => ({ ...prev, cuerpo: prev.cuerpo + key }));
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Plantillas de texto</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={handleOpenCreate}>
          Nueva plantilla
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Nombre</strong></TableCell>
                <TableCell><strong>Tipo de cliente</strong></TableCell>
                <TableCell><strong>Estado</strong></TableCell>
                <TableCell><strong>Vista previa</strong></TableCell>
                <TableCell align="right"><strong>Acciones</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {plantillas.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                    No hay plantillas creadas. Creá la primera con el botón de arriba.
                  </TableCell>
                </TableRow>
              ) : (
                plantillas.map(p => (
                  <TableRow key={p.id} hover>
                    <TableCell>{p.nombre}</TableCell>
                    <TableCell>
                      <Chip
                        label={getTipoLabel(p.tipo_cliente)}
                        size="small"
                        color={p.tipo_cliente === null ? 'default' : 'primary'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={p.activa ? 'Activa' : 'Inactiva'}
                        size="small"
                        color={p.activa ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {p.cuerpo.substring(0, 80)}...
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => handleOpenEdit(p)}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Eliminar">
                        <IconButton size="small" color="error" onClick={() => handleOpenDelete(p.id)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialog crear/editar */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingId ? 'Editar plantilla' : 'Nueva plantilla'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField
            label="Nombre de la plantilla"
            value={form.nombre}
            onChange={e => setForm(prev => ({ ...prev, nombre: e.target.value }))}
            fullWidth
            required
          />
          <FormControl fullWidth>
            <InputLabel>Tipo de cliente</InputLabel>
            <Select
              value={form.tipo_cliente}
              label="Tipo de cliente"
              onChange={e => setForm(prev => ({ ...prev, tipo_cliente: e.target.value }))}
            >
              
              {TIPO_OPTIONS.map(t => (
                <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Estado</InputLabel>
            <Select
              value={form.activa}
              label="Estado"
              onChange={e => setForm(prev => ({ ...prev, activa: e.target.value }))}
            >
              <MenuItem value={1}>Activa</MenuItem>
              <MenuItem value={0}>Inactiva</MenuItem>
            </Select>
          </FormControl>

          {/* Placeholders disponibles */}
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <InfoOutlined fontSize="inherit" /> Hacé clic en un placeholder para insertarlo en el cuerpo:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {PLACEHOLDERS.map(p => (
                <Tooltip key={p.key} title={p.desc}>
                  <Chip
                    label={p.key}
                    size="small"
                    onClick={() => insertPlaceholder(p.key)}
                    clickable
                    variant="outlined"
                    color="primary"
                  />
                </Tooltip>
              ))}
            </Box>
          </Box>

          <TextField
            label="Cuerpo del comunicado"
            value={form.cuerpo}
            onChange={e => setForm(prev => ({ ...prev, cuerpo: e.target.value }))}
            multiline
            rows={12}
            fullWidth
            required
            inputProps={{ style: { fontFamily: 'monospace', fontSize: 13 } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog confirmar eliminación */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Eliminar plantilla</DialogTitle>
        <DialogContent>
          <Typography>¿Estás seguro de que querés eliminar esta plantilla? Esta acción no se puede deshacer.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Eliminar</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
